import { useState, useEffect, useCallback, useMemo } from 'react';
import { projectApi } from '../../../../api/projectApi';
import { leaderApi } from '../../../../api/leaderApi';
import { employeeApi } from '../../../../api/employeeApi';
import { useToast } from '../../../../context/ToastContext';
import { checkRecurringOverlap } from '../../../../utils/leaderUtils';

/**
 * @const defaultPermissions
 * @description Standard-Berechtigungen für neue Team-Mitglieder.
 */
const defaultPermissions = {
    journal: {
        mode: 'draft',
        full_level: 'read',
        draft_visibility: 'own',
        draft_action: 'own'
    },
    team: { manage: false },
    settings: { edit_project_data: false }
};

/**
 * @hook useProjectTeam
 * @description Central logic for managing project resource assignments (workers, vehicles, and kolonnen).
 * Implements an intelligent, derived-state synchronization model for Kolonnen tracking and 
 * ensures atomic cross-role transitions (e.g., project team to blacklist).
 * 
 * @param {Object} params
 * @param {Object} params.project - The current project object.
 * @param {Object} params.currentUser - The logged-in user.
 * @param {Array} [params.tempTeamMembers=[]] - Initial/temporary team state for new projects.
 * @param {Function} params.onTeamChange - Callback for state synchronization with parent.
 * @param {boolean} [params.readOnly=false] - If true, all modifying actions are disabled.
 * 
 * @returns {Object} {
 *   assignedUsers, availableWorkers, availableVehicles, availableCategories, availableKolonnen,
 *   assignedKolonnen, assignedIds, teamView, setTeamView, sidebarTab, setSidebarTab,
 *   handleToggleResource, handleRemoveUser, handleRemoveKolonne, setPermission, ...
 * }
 */
export function useProjectTeam({ project, currentUser, tempTeamMembers = [], onTeamChange, readOnly, initialTeamTabs }) {
    const { showToast } = useToast();
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [availableWorkers, setAvailableWorkers] = useState([]);
    const [availableVehicles, setAvailableVehicles] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableKolonnen, setAvailableKolonnen] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [absences, setAbsences] = useState([]);

    // UI States
    const [teamView, setTeamView] = useState(initialTeamTabs?.teamView || 'team'); // 'team' | 'blacklist'
    const [activeTab, setActiveTab] = useState(initialTeamTabs?.activeTab || 'personal'); // 'personal' | 'fahrzeuge' | 'kolonnen'
    const [sidebarTab, setSidebarTab] = useState(initialTeamTabs?.sidebarTab || 'personal');
    const [myPerms, setMyPerms] = useState({ p_manage: false, is_admin: false });

    // React pitfall: initialTeamTabs might be set after the first render.
    useEffect(() => {
        if (initialTeamTabs) {
            if (initialTeamTabs.teamView) setTeamView(initialTeamTabs.teamView);
            if (initialTeamTabs.activeTab) setActiveTab(initialTeamTabs.activeTab);
            if (initialTeamTabs.sidebarTab) setSidebarTab(initialTeamTabs.sidebarTab);
        }
    }, [initialTeamTabs]);

    const [userForm, setUserForm] = useState({
        user_id: '',
        worker_id: '',
        role: '',
        permissions: JSON.parse(JSON.stringify(defaultPermissions))
    });

    const loadData = useCallback(async () => {
        try {
            const [users, workers, vehicles, categories, kolonnen, absencesData] = await Promise.all([
                project?.id ? projectApi.getProjectUsers(project.id) : Promise.resolve(tempTeamMembers),
                employeeApi.getEmployees(),
                leaderApi.getVehicles(),
                leaderApi.getVehicleCategories(),
                leaderApi.getKolonnen(),
                leaderApi.getAbsences()
            ]);

            const enrichedUsers = users.map(u => {
                const storedRole = u.project_role || u.role || '';
                const isVehicle = storedRole === 'Fahrzeug';
                const worker = !isVehicle ? workers.find(w => String(w.id) === String(u.worker_id)) : null;
                const vehicle = isVehicle ? vehicles.find(v => String(v.id) === String(u.worker_id)) : null;
                const plate = isVehicle ? (u.plate || vehicle?.plate || vehicle?.license_plate) : null;

                const hasSavedRole = storedRole && storedRole !== 'employee' && storedRole !== 'blacklisted' && storedRole !== 'Fahrzeug';
                const defaultRole = worker?.category_name || worker?.role || '';
                const finalRole = hasSavedRole ? storedRole : defaultRole;

                return {
                    ...u,
                    category_id: u.category_id || vehicle?.category_id || null, // Preserve category_id
                    global_role: u.global_role || worker?.role || vehicle?.category_name || vehicle?.vehicle_type || (isVehicle ? 'Fahrzeug' : 'Personal'),
                    employee_role: worker?.category_name || worker?.role || 'Mitglied',
                    name: u.name || worker?.name || plate || vehicle?.internal_name || 'Unbekannt',
                    plate: plate || null,
                    internal_name: u.internal_name || vehicle?.internal_name || null,
                    project_role: storedRole || (isVehicle ? 'Fahrzeug' : 'employee'),
                    role: isVehicle ? '' : (u.role || finalRole)
                };
            });

            const sortedCategories = (categories || []).sort((a, b) => {
                if (a.name === 'Sonstiges') return 1;
                if (b.name === 'Sonstiges') return -1;
                return a.name.localeCompare(b.name);
            });

            setAssignedUsers(enrichedUsers);
            setAvailableWorkers(workers);
            setAvailableVehicles(vehicles);
            setAvailableCategories(sortedCategories);
            setAvailableKolonnen(kolonnen);
            setAbsences(absencesData);

            if (project?.id) {
                const me = enrichedUsers.find(u => String(u.user_id) === String(currentUser?.id));
                setMyPerms({
                    p_manage: !!(me?.p_manage || me?.project_role === 'project_admin'),
                    is_admin: currentUser?.role === 'admin'
                });
            } else {
                setMyPerms({ p_manage: true, is_admin: currentUser?.role === 'admin' });
            }
        } catch (err) {
            console.error('Error loading team data:', err);
            showToast('Fehler beim Laden der Team-Daten', 'error');
        }
    }, [project, currentUser, showToast, tempTeamMembers]);


    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleUserSelect = useCallback((u) => {
        setEditingUser(u);
        const perms = u.permissions || JSON.parse(JSON.stringify(defaultPermissions));
        const storedRole = u.project_role || u.role || '';
        const initialRole = storedRole === 'Fahrzeug' ? (u.global_role === 'Fahrzeug' ? '' : u.global_role) : u.role || '';

        setUserForm({
            user_id: u.user_id || '',
            worker_id: u.worker_id || '',
            role: initialRole === 'Bauleiter' || initialRole === 'project_admin' ? 'Bauleiter' : initialRole,
            permissions: JSON.parse(JSON.stringify(perms))
        });
    }, []);

    const handleToggleResource = async (resource, forcedType = null) => {
        if (readOnly) return;

        const resourceIdStr = String(resource.id || resource.worker_id);
        const isKolonne = !!resource.members || forcedType === 'kolonne';

        // Use forcedType if provided, otherwise fallback to detection logic
        let type = forcedType;
        if (!type) {
            const isVehicleRes = !!(resource.plate || resource.license_plate || resource.global_role === 'Fahrzeug' || resource.type === 'vehicle');
            type = isKolonne ? 'kolonne' : (isVehicleRes ? 'vehicle' : 'worker');
        }

        const prefix = type === 'kolonne' ? 'kolonne_' : (type === 'vehicle' ? 'vehicle_' : 'worker_');
        const typeSafeId = `${prefix}${resourceIdStr}`;

        // Handle Removal
        // We re-check assigned status here to avoid stale closure issues
        const currentIsAssigned = type === 'kolonne'
            ? assignedKolonnen.some(k => String(k.id) === resourceIdStr)
            : assignedIds.includes(typeSafeId);

        if (currentIsAssigned) {
            if (isKolonne) {
                return handleRemoveKolonne(resource);
            } else {
                // Find the exact user to remove based on ID AND Role
                let targetRole = type === 'vehicle' ? 'Fahrzeug' : 'employee';
                if (teamView === 'blacklist') targetRole = 'blacklisted';

                const userToRemove = assignedUsers.find(u =>
                    String(u.worker_id) === resourceIdStr &&
                    (u.project_role || u.role) === targetRole
                );
                if (userToRemove) return handleRemoveUser(userToRemove, true, type);
            }
            return;
        }

        // Handle Addition
        try {
            let resourcesToAdd = [];
            let currentAssignedUsers = [...assignedUsers];

            if (type === 'kolonne') {
                const members = resource.members || [];
                const blacklistedInKolonne = members.filter(m =>
                    m.type === 'worker' &&
                    currentAssignedUsers.some(u => String(u.worker_id) === String(m.resource_id) && (u.project_role || u.role) === 'blacklisted')
                );

                if (blacklistedInKolonne.length > 0) {
                    const names = blacklistedInKolonne.map(m => {
                        const w = availableWorkers.find(worker => String(worker.id) === String(m.resource_id));
                        return w ? w.name : 'Unbekannt';
                    }).join(', ');

                    if (!window.confirm(`Achtung: ${names} stehen auf der Blacklist. Trotzdem hinzufügen und von der Blacklist entfernen?`)) {
                        return;
                    }

                    // Remove them from blacklist first
                    for (const m of blacklistedInKolonne) {
                        const existingId = String(m.resource_id);
                        const existing = currentAssignedUsers.find(u => String(u.worker_id) === existingId && (u.project_role || u.role) === 'blacklisted');
                        if (existing) {
                            await handleRemoveUser(existing, true, 'worker');
                            // Update local list to allow re-addition
                            currentAssignedUsers = currentAssignedUsers.filter(u => !(String(u.worker_id) === existingId && (u.project_role || u.role) === 'blacklisted'));
                        }
                    }
                }

                resourcesToAdd = members.map(m => {
                    if (m.type === 'worker') return availableWorkers.find(w => String(w.id) === String(m.resource_id));
                    if (m.type === 'vehicle') return availableVehicles.find(v => String(v.id) === String(m.resource_id));
                    return null;
                }).filter(Boolean);
            } else {
                // Individual resource addition
                // Check for cross-role conflicts
                if (teamView === 'team') {
                    const existingBlacklisted = currentAssignedUsers.find(u => String(u.worker_id) === resourceIdStr && (u.project_role || u.role) === 'blacklisted');
                    if (existingBlacklisted) {
                        if (!window.confirm(`Achtung: ${resource.name || 'Diese Person'} steht auf der Blacklist. Von der Blacklist entfernen und zum Team hinzufügen?`)) return;
                        await handleRemoveUser(existingBlacklisted, true, 'worker');
                        // Update local list to allow re-addition
                        currentAssignedUsers = currentAssignedUsers.filter(u => !(String(u.worker_id) === resourceIdStr && (u.project_role || u.role) === 'blacklisted'));
                    }
                } else if (teamView === 'blacklist') {
                    const existingInTeam = currentAssignedUsers.find(u => String(u.worker_id) === resourceIdStr && (u.project_role || u.role) === 'employee');
                    if (existingInTeam) {
                        if (!window.confirm(`Achtung: ${resource.name || 'Diese Person'} ist bereits im Projekt-Team. Aus dem Team entfernen und auf die Blacklist setzen?`)) return;
                        await handleRemoveUser(existingInTeam, true, 'worker');
                        // Update local list to allow re-addition
                        currentAssignedUsers = currentAssignedUsers.filter(u => !(String(u.worker_id) === resourceIdStr && (u.project_role || u.role) === 'employee'));
                    }
                }

                resourcesToAdd = [resource];
            }

            const newMembers = [];
            for (const res of resourcesToAdd) {
                const finalWorkerId = String(res.worker_id || res.id);
                // CRITICAL FIX: Check individual resource type, not just the parent 'type'
                const isActuallyVehicle = res.license_plate || res.plate || res.global_role === 'Fahrzeug' || res.type === 'vehicle';
                const currentResProjectRole = teamView === 'blacklist' ? 'blacklisted' : (isActuallyVehicle ? 'Fahrzeug' : 'employee');

                // Check if this specific combination (ID + Role) is already assigned using the UPDATED local list
                const isAlreadyAssigned = currentAssignedUsers.some(u => {
                    if (String(u.worker_id) !== finalWorkerId) return false;
                    const storedRole = u.project_role || u.role;
                    if (isActuallyVehicle) {
                        return storedRole === 'Fahrzeug';
                    } else {
                        if (teamView === 'blacklist') {
                            return storedRole === 'blacklisted';
                        } else {
                            // Any non-blacklisted, non-vehicle role means they are already in the team!
                            return storedRole !== 'blacklisted' && storedRole !== 'Fahrzeug';
                        }
                    }
                });
                if (isAlreadyAssigned) continue;

                const payload = {
                    user_id: res.user_id || null,
                    worker_id: finalWorkerId,
                    role: currentResProjectRole,
                    project_role: currentResProjectRole,
                    permissions: (res.user_id && !isActuallyVehicle)
                        ? { journal: { mode: 'full', full_level: 'read', draft_visibility: 'own', draft_action: 'own' }, team: { manage: false }, settings: { edit_project_data: false } }
                        : JSON.parse(JSON.stringify(defaultPermissions)),
                    name: res.name || res.plate,
                    plate: res.plate || res.license_plate || null,
                    color: res.color,
                    global_role: res.category_name || res.role || res.global_role || (isActuallyVehicle ? 'Fahrzeug' : 'Personal'),
                    role: isActuallyVehicle ? '' : (res.category_name || res.role || res.global_role || ''),
                    employee_role: isActuallyVehicle ? (res.category_name || res.vehicle_type || 'Fahrzeug') : (res.category_name || res.role || 'Mitglied'),
                    is_creator: res.user_id === currentUser?.id
                };

                if (project?.id) await projectApi.addProjectUser(project.id, payload);
                newMembers.push(payload);
            }

            if (newMembers.length > 0 || type === 'kolonne') {
                // Update users
                setAssignedUsers([...currentAssignedUsers, ...newMembers]);

                if (!project?.id) onTeamChange([...currentAssignedUsers, ...newMembers]);
                if (newMembers.length > 0) handleUserSelect(newMembers[0]);
                showToast(`${newMembers.length} Ressource(n) ${teamView === 'blacklist' ? 'geblockt' : 'hinzugefügt'}`, 'success');
            }
        } catch (err) {
            console.error('Toggle Error:', err);
            showToast('Fehler beim Zuweisen', 'error');
        }
    };

    const handleRemoveUser = async (user = editingUser, noConfirm = false, forcedType = null) => {
        if (readOnly) return;
        if (!user) return;

        const workerIdStr = String(user.worker_id || user.id);
        const displayName = user.plate || user.name || 'Ressource';

        if (!noConfirm && !window.confirm(`Möchtest du ${displayName} wirklich entfernen?`)) return;

        try {
            // 1. Determine the role of the user to be removed to avoid removing "ID twins"
            let userToRemoveRole = user.project_role || user.role;
            if (!userToRemoveRole) {
                if (teamView === 'blacklist') userToRemoveRole = 'blacklisted';
                else userToRemoveRole = forcedType === 'vehicle' ? 'Fahrzeug' : 'employee';
            }
            const userToRemoveId = user.user_id || workerIdStr;

            if (project?.id) await projectApi.removeProjectUser(project.id, userToRemoveId);

            const updatedUsers = assignedUsers.filter(u =>
                user.user_id
                    ? String(u.user_id || '') !== String(user.user_id)
                    : !(String(u.worker_id) === userToRemoveId && (u.project_role || u.role) === userToRemoveRole)
            );

            setAssignedUsers(updatedUsers);
            if (!project?.id) onTeamChange(updatedUsers);

            setEditingUser(null);
            showToast(`${displayName} entfernt`, 'info');
        } catch (err) {
            console.error('Remove Error:', err);
            showToast('Fehler beim Entfernen', 'error');
        }
    };

    const handleRemoveKolonne = async (kolonne) => {
        if (readOnly) return;
        if (!window.confirm(`Möchtest du die Kolonne ${kolonne.name} und alle ihre Mitglieder aus dem Projekt entfernen?`)) return;

        try {
            const memberIds = (kolonne.members || []).map(m => String(m.resource_id));

            // Remove each member from the project state
            let updatedUsers = [...assignedUsers];
            for (const mId of memberIds) {
                // Remove from backend if project exists
                if (project?.id) {
                    // Find any entry (worker or vehicle) with this ID and remove it
                    const entries = updatedUsers.filter(u => String(u.worker_id) === mId);
                    for (const entry of entries) {
                        await projectApi.removeProjectUser(project.id, entry.worker_id);
                    }
                }
                // Filter locally
                updatedUsers = updatedUsers.filter(u => String(u.worker_id) !== mId);
            }

            setAssignedUsers(updatedUsers);
            if (!project?.id) onTeamChange(updatedUsers);
            setEditingUser(null);
            showToast(`Kolonne '${kolonne.name}' und Mitglieder entfernt`, 'info');
        } catch (err) {
            console.error('Remove Kolonne Error:', err);
            showToast('Fehler beim Entfernen der Kolonne', 'error');
        }
    };

    const handleRemoveAllResources = async () => {
        if (readOnly) return;
        if (assignedUsers.length === 0) {
            showToast('Keine Ressourcen zum Entfernen vorhanden', 'info');
            return;
        }

        if (!window.confirm('Möchtest du wirklich alle zugewiesenen Ressourcen (Personal, Fahrzeuge und Kolonnen) aus dem Projekt entfernen?')) {
            return;
        }

        try {
            if (project?.id) {
                const removePromises = assignedUsers.map(u => 
                    projectApi.removeProjectUser(project.id, u.user_id || u.worker_id)
                );
                await Promise.all(removePromises);
            }

            setAssignedUsers([]);
            if (!project?.id) onTeamChange([]);
            setEditingUser(null);
            showToast('Alle Ressourcen erfolgreich entfernt', 'success');
        } catch (err) {
            console.error('Remove All Error:', err);
            showToast('Fehler beim Entfernen aller Ressourcen', 'error');
        }
    };

    const setPermission = (category, key, val) => {
        if (readOnly) return;
        setUserForm(prev => ({
            ...prev,
            permissions: { ...prev.permissions, [category]: { ...prev.permissions[category], [key]: val } }
        }));
    };

    const setJournalValue = (key, value) => {
        if (readOnly) return;
        setUserForm(prev => {
            const p = { ...prev.permissions };
            p.journal = { ...(p.journal || {}), [key]: value };
            return { ...prev, permissions: p };
        });
    };

    const handleSubmitPermissions = async (e) => {
        if (e) e.preventDefault();
        if (readOnly || !editingUser) return;
        try {
            const editingUserRole = editingUser.project_role || editingUser.role || '';
            const finalRole = userForm.role || (editingUserRole === 'Fahrzeug' ? 'Fahrzeug' : 'employee');
            const permissions = JSON.parse(JSON.stringify(userForm.permissions));
            if (permissions.journal?.mode === 'draft') {
                permissions.journal = {
                    ...(permissions.journal || {}),
                    draft_visibility: 'own',
                    draft_action: 'own'
                };
            }
            const payload = {
                user_id: userForm.user_id || null,
                worker_id: userForm.worker_id,
                role: finalRole,
                project_role: finalRole,
                permissions
            };
            if (project?.id) await projectApi.addProjectUser(project.id, payload);
            const updatedTeam = assignedUsers.map(u => (String(u.worker_id) === String(userForm.worker_id)) ? { ...u, project_role: payload.project_role, role: payload.project_role, permissions: payload.permissions } : u);
            setAssignedUsers(updatedTeam);
            if (!project?.id) onTeamChange(updatedTeam);
            showToast('Berechtigungen gespeichert', 'success');
        } catch (err) {
            console.error('Submit Permissions Error:', err);
            showToast('Fehler beim Speichern', 'error');
        }
    };

    // Computed State: Which Kolonnen are currently "assigned" (all members present in project)
    const assignedKolonnen = useMemo(() => {
        return availableKolonnen.filter(kolonne => {
            const memberIds = (kolonne.members || []).map(m => String(m.resource_id));
            if (memberIds.length === 0) return false;

            // A Kolonne is considered assigned if ALL its members are in assignedUsers (as employees or vehicles)
            return memberIds.every(mId =>
                assignedUsers.some(u => String(u.worker_id) === mId && (u.project_role || u.role) !== 'blacklisted')
            );
        }).map(k => ({
            ...k,
            memberIds: (k.members || []).map(m => String(m.resource_id))
        }));
    }, [availableKolonnen, assignedUsers]);

    const assignedIds = useMemo(() => {
        const ids = assignedUsers.map(u => {
            const storedRole = u.project_role || u.role;
            const prefix = storedRole === 'Fahrzeug' ? 'vehicle_' : 'worker_';

            // Contextual hiding:
            // If we are in team view, only hide resources that are actually in the team (not blacklisted)
            if (teamView === 'team' && storedRole === 'blacklisted') return null;
            // If we are in blacklist view, only hide resources that are already blacklisted
            if (teamView === 'blacklist' && storedRole !== 'blacklisted') return null;

            return `${prefix}${u.worker_id}`;
        }).filter(Boolean);

        // Add assigned kolonnen to the hide-list
        assignedKolonnen.forEach(k => {
            ids.push(`kolonne_${k.id}`);
        });

        return ids;
    }, [assignedUsers, assignedKolonnen, teamView]);

    const displayList = useMemo(() => {
        // Full lists for counts
        const allWorkers = assignedUsers.filter(u => (u.project_role || u.role) !== 'blacklisted' && (u.project_role || u.role) !== 'Fahrzeug');
        const allVehicles = assignedUsers.filter(u => (u.project_role || u.role) === 'Fahrzeug');

        // Filtered list for current view
        const filtered = assignedUsers.filter(u => {
            const storedRole = u.project_role || u.role;
            if (teamView === 'blacklist') return storedRole === 'blacklisted';
            if (teamView === 'team') {
                if (storedRole === 'blacklisted') return false;
                if (activeTab === 'fahrzeuge') return storedRole === 'Fahrzeug';
                if (activeTab === 'personal') return storedRole !== 'Fahrzeug';
                return true;
            }
            return false;
        });

        return {
            workers: allWorkers, // Use all for counts
            vehicles: allVehicles, // Use all for counts
            viewItems: filtered, // Items to actually render in the list
            kolonnen: assignedKolonnen.map(ak => {
                const original = availableKolonnen.find(k => String(k.id) === String(ak.id));
                return {
                    ...original,
                    ...ak,
                    id: ak.id,
                    name: ak.name || original?.name,
                    memberIds: ak.memberIds || original?.members?.map(m => String(m.resource_id)) || []
                };
            })
        };
    }, [assignedUsers, teamView, activeTab, assignedKolonnen, availableKolonnen]);

    /** Get status badge for a worker (Free, Absent) */
    const getWorkerStatus = useCallback((workerId) => {
        const workerAbs = (absences || []).filter(a => String(a.worker_id) === String(workerId));
        const now = new Date();
        const dummyAssignment = {
            start_datetime: now,
            end_datetime: now,
            all_day: false
        };

        let activeAbsence = null;
        try {
            for (const abs of workerAbs) {
                if (checkRecurringOverlap(abs, dummyAssignment)) {
                    activeAbsence = abs;
                    break;
                }
            }
        } catch (e) { console.error("Status check error", e); }

        if (activeAbsence) {
            let type = (activeAbsence.absence_type || '').toLowerCase();
            if (type === 'dienstreise') type = 'termin';
            
            if (type === 'urlaub') return { label: 'Urlaub', dotColor: '#007aff' };
            if (type === 'krank') return { label: 'Krankheit', dotColor: '#ff3b30' };
            if (type === 'termin') return { label: 'Termin', dotColor: '#ff9500' };
            return { label: 'Sonstiges', dotColor: '#af52de' };
        }

        return { label: 'Anwesend', dotClass: 'bg-green-500' };
    }, [absences]);

    /** Get status badge for a vehicle */
    const getVehicleStatus = useCallback((vehicleId) => {
        const vehicle = (availableVehicles || []).find(v => String(v.id) === String(vehicleId));
        if (!vehicle) return null;

        const status = vehicle.status || 'green';
        if (status === 'red') return { label: 'Defekt / Werkstatt', dotColor: '#ef4444' };
        if (status === 'yellow') return { label: 'Eingeschränkt', dotColor: '#f59e0b' };
        return { label: 'Einsatzbereit', dotColor: '#10b981' };
    }, [availableVehicles]);

    return {
        assignedUsers,
        assignedKolonnen,
        availableWorkers,
        availableVehicles,
        availableCategories,
        availableKolonnen,
        editingUser,
        teamView,
        activeTab,
        sidebarTab,
        myPerms,
        userForm,
        assignedIds,
        displayList,
        setTeamView,
        setActiveTab,
        setSidebarTab,
        setEditingUser,
        setUserForm,
        handleUserSelect,
        handleToggleResource,
        handleRemoveUser,
        handleRemoveKolonne,
        handleRemoveAllResources,
        setPermission,
        setJournalValue,
        handleSubmitPermissions,
        getWorkerStatus,
        getVehicleStatus
    };
}
